import { FiArrowUpRight, FiCheck, FiCompass, FiHome, FiMapPin } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { property, propertyAddress } from '../config/property';

const facts = [
  ['$400,000', 'Asking price'],
  ['3', 'Bedrooms'],
  ['2', 'Bathrooms'],
  ['1,644', 'Square feet'],
  ['0.18 ac', 'Lot size'],
  ['2023', 'Year built'],
  ['$206/qtr', 'HOA (about $69/mo)'],
];

export function QuickFacts() {
  return (
    <section className="facts-bar" aria-label="Property facts">
      {facts.map(([value, label]) => <div className="fact" key={label}><strong>{value}</strong><span>{label}</span></div>)}
    </section>
  );
}

export function Introduction() {
  return (
    <section className="section intro-section">
      <div className="intro-copy">
        <p className="eyebrow">The residence</p>
        <h2>Modern comfort, thoughtfully laid out.</h2>
        <p>Built in 2023, this single-family home offers 1,644 square feet with three bedrooms and two bathrooms. Its cul-de-sac location, one-level layout, and open living areas create a natural flow through the home.</p>
        <p>Explore the kitchen and primary suite, then see whether 3649 Azalea Ridge Dr feels right for your next move. The property sits on a 0.18-acre lot in Mount Holly, with the greater Belmont and Charlotte area within reach.</p>
        <a className="text-link" href="#photos">Explore property photos <SafeIcon icon={FiArrowUpRight} /></a>
      </div>
      <div className="intro-aside">
        <div><span>01</span><p>Single-level living</p></div>
        <div><span>02</span><p>Built in 2023</p></div>
        <div><span>03</span><p>1,644 sq. ft.</p></div>
      </div>
    </section>
  );
}

export function Highlights() {
  const highlights = [
    ['Built in 2023', 'Recent construction', FiHome],
    ['Single-level', 'One-level layout', FiCheck],
    ['Cul-de-Sac Location', 'Located on a cul-de-sac', FiMapPin],
    ['Open living areas', 'Room to move through the home', FiCompass],
    ['0.18-acre lot', 'Property lot size', FiMapPin],
  ];

  return (
    <section className="section highlights-section">
      <div className="section-heading">
        <p className="eyebrow">At a glance</p>
        <h2>Details worth knowing</h2>
      </div>
      <div className="highlight-grid">
        {highlights.map(([title, text, icon]) => (
          <article className="highlight-card" key={title}>
            <span className="highlight-icon"><SafeIcon icon={icon} /></span>
            <h3>{title}</h3>
            <p>{text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

const locations = [
  ['Downtown Mount Holly', 'Explore local dining, shops and businesses in Mount Holly.'],
  ['Belmont', 'Belmont is part of the nearby regional area.'],
  ['Charlotte', 'A Mount Holly address with access to the greater Charlotte area.'],
  ['Charlotte Douglas International Airport', 'The airport serves the wider Charlotte region.'],
];

export function LocationSection() {
  return (
    <section className="section location-section" id="location">
      <div className="location-top">
        <div className="section-heading">
          <p className="eyebrow">The neighborhood</p>
          <h2>Convenient Mount Holly location</h2>
          <p>Explore Mount Holly and the surrounding area from this address.</p>
        </div>
        <span className="location-stamp"><SafeIcon icon={FiMapPin} /> Gaston County, NC</span>
      </div>
      <div className="location-grid">
        {locations.map(([title, text]) => <article className="location-card" key={title}><span><SafeIcon icon={FiCompass} /></span><h3>{title}</h3><p>{text}</p></article>)}
      </div>
      <div className="map-card">
        <iframe title={`Map showing ${propertyAddress}`} src={property.mapEmbedUrl} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        <div className="map-label"><SafeIcon icon={FiMapPin} /><span>{propertyAddress}</span></div>
      </div>
    </section>
  );
}

const questions = [
  ['What is the asking price?', '$400,000.'],
  ['How many bedrooms and bathrooms?', 'The home has 3 bedrooms and 2 bathrooms.'],
  ['How large is the home?', 'The listed living area is 1,644 square feet, on a 0.18-acre lot.'],
  ['When was the home built?', 'The year built is 2023.'],
  ['Is there an HOA?', 'The HOA is $206 quarterly, approximately $69 per month. Verify current dues with the listing team.'],
  ['How do I request a showing?', 'Use the inquiry form to request a private showing. A request is not confirmed until someone follows up.'],
  ['Can I request more property information?', 'Yes. Choose “Property question” in the inquiry form and send your request.'],
];

export function FAQ() {
  return (
    <section className="section faq-section">
      <div className="section-heading">
        <p className="eyebrow">Good to know</p>
        <h2>Frequently asked questions</h2>
      </div>
      <div className="faq-list">
        {questions.map(([question, answer]) => <details key={question}><summary>{question}</summary><p>{answer}</p></details>)}
      </div>
    </section>
  );
}